/**
 * **Halaman pengisian harga** — satu sasaran, banyak produk, banyak kanal.
 *
 * Imron (2026-08-04): *"pembuatan harga di price group saja, karena butuh masukkan harga ke banyak
 * channel. harga di product crud diberi keterangan, masukkan harga lewat price group page."*
 *
 * ### ⭐ Kenapa harga PINDAH ke sini, bukan tetap di laci produk
 * Laci produk menjawab *"berapa harga produk ini, di kanal apa, sejak kapan"* — satu produk,
 * banyak waktu. Yang sebenarnya dikerjakan orang saat menyiapkan menu adalah kebalikannya: **satu
 * sasaran, banyak produk, beberapa kanal sekaligus** (dine-in vs GoFood vs Grab). Lewat laci
 * produk itu berarti 300 laci × 6 kanal, dan yang mengerjakannya berhenti di produk ke-20.
 *
 * ### ⛔⛔ "Harga umum" WAJIB jadi salah satu sasaran di sini
 * Kalau halaman ini hanya melayani grup, memindahkan pengisian ke sini berarti harga umum — yang
 * dipakai setiap outlet yang tak masuk grup mana pun — tak punya jalan masuk sama sekali, dan
 * tenant baru tak bisa memberi harga apa pun. Itu bukan penyederhanaan, itu penghapusan fitur.
 *
 * ### ⚠️ Sasaran ganda tak pernah ditulis dalam satu baris
 * `product_price` menegakkan CHECK satu-sasaran: outlet, ATAU grup, ATAU umum. Baris bersasaran
 * ganda tak punya jawaban tunggal atas "ini harga untuk siapa?".
 *
 * ### ⛔ Menyimpan = TUTUP baris lama + BUAT baris baru
 * Menimpa `price` menghapus jawaban atas "berapa harganya bulan lalu?" — pertanyaan yang selalu
 * datang saat ada yang membantah struk.
 */
import {
  Alert, Button, Empty, Input, Popconfirm, Select, Space, Table, Tag, Typography, message,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PRICE_CHANNELS } from '@osbpos/contract';

import { api } from '../api/client';
import type { Price, PriceGroup, Product } from '../api/client';
import { canWrite } from '../api/session';

/**
 * Tanggal **lokal** `YYYY-MM-DD`.
 *
 * ⛔⛔ Bukan `new Date().toISOString().slice(0,10)` — itu tanggal **UTC**, dan di UTC+7 ia
 * meleset satu hari setiap pukul 00:00–07:00 waktu setempat. Akibatnya bukan sekadar label salah:
 * `valid_from` harga yang diisi pukul 01:00 mundur ke hari KEMARIN, dan pemeriksaan "baris ini
 * dibuat hari ini?" di bawah ikut mundur — sehingga harga KEMARIN yang seharusnya cuma DITUTUP
 * malah **dihapus**, bersama jawaban atas "berapa harganya kemarin?".
 *
 * ⚠️ Yang tersisa dan sengaja tak dimodelkan: `day_cutover_time` milik OUTLET. Harga di sini
 * bersasaran tenant/grup — ia tak punya satu outlet untuk ditanya, dan mengarang satu outlet
 * demi cutover lebih menyesatkan daripada memakai tanggal kalender apa adanya.
 */
function tanggalLokal(): string {
  const d = new Date();
  const bulan = String(d.getMonth() + 1).padStart(2, '0');
  const hari = String(d.getDate()).padStart(2, '0');
  return `${String(d.getFullYear())}-${bulan}-${hari}`;
}

/** Sasaran harga. ⛔ `null` = **harga umum** — bukan "belum dipilih". */
export type PriceTarget = PriceGroup | null;

export interface PriceMatrixProps {
  readonly brandId: string;
  readonly groups: readonly PriceGroup[];
}

interface Baris {
  readonly product: Product;
  /** `channel → harga berjalan pada SASARAN terpilih`. */
  readonly nilai: Readonly<Record<string, Price | undefined>>;
  /** `channel → harga UMUM berjalan` — pembanding saat sasarannya sebuah grup. */
  readonly umum: Readonly<Record<string, Price | undefined>>;
}

function rp(v: string): string {
  return Number(v).toLocaleString('id-ID');
}

export function PriceMatrix({ brandId, groups }: PriceMatrixProps) {
  const { t } = useTranslation();
  const boleh = canWrite();

  const [targetId, setTargetId] = useState<string>('');
  /*
   * ⚠️ Kanal dipilih, tidak semuanya ditampilkan sekaligus. Enam kolom × 300 baris menghasilkan
   * tabel yang harus digeser mendatar untuk dibaca — dan yang harus digeser tak akan diperiksa.
   * Bawaannya `dine_in`: kanal yang selalu ada di tiap outlet.
   */
  const [kanal, setKanal] = useState<readonly string[]>(['dine_in']);
  const [baris, setBaris] = useState<readonly Baris[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [cari, setCari] = useState('');
  const [memuat, setMemuat] = useState(false);

  const target: PriceTarget = targetId === '' ? null : (groups.find((g) => g.id === targetId) ?? null);

  const muat = useCallback(async () => {
    if (kanal.length === 0) { setBaris([]); return; }
    setMemuat(true);
    try {
      /*
       * ⚠️ Produk milik KATEGORI → DEPARTEMEN → BRAND, dan harga selalu disetel dalam konteks
       * sebuah brand. Disaring lewat rantai itu, bukan diambil seluruh tenant: produk brand lain
       * yang muncul di sini akan diberi harga oleh seseorang suatu hari.
       */
      const dept = await api.departments(brandId, true);
      const kategori = (await Promise.all(dept.map((d) => api.categories(d.id, true)))).flat();
      const milikBrand = new Set(kategori.map((k) => k.id));

      const [produk, ...perKanal] = await Promise.all([
        api.products(undefined, true),
        ...kanal.map((c) => api.pricesByChannel(c)),
      ]);
      const semuaHarga = perKanal.flat();
      const dipakai = produk.filter((p) => milikBrand.has(p.category_id));

      const berjalan = (pid: string, c: string, grupId: string | null) =>
        semuaHarga.find(
          (x) => x.product_id === pid && x.channel === c && x.valid_to === null
            && x.outlet_id === null && x.price_group_id === grupId,
        );

      setBaris(dipakai.map((p) => ({
        product: p,
        nilai: Object.fromEntries(kanal.map((c) => [c, berjalan(p.id, c, targetId === '' ? null : targetId)])),
        umum: Object.fromEntries(kanal.map((c) => [c, berjalan(p.id, c, null)])),
      })));
      setDraft({});
    } finally {
      setMemuat(false);
    }
  }, [brandId, kanal, targetId]);

  useEffect(() => { void muat().catch(() => undefined); }, [muat]);

  const kunci = (pid: string, c: string) => `${pid}|${c}`;

  /**
   * ⭐⭐⭐ **Satu-satunya penulis harga** — dipakai `simpan()` maupun `replikasi()`.
   *
   * ### ⛔⛔ Kenapa baris HARI INI dibuang, bukan ditutup
   * CHECK `product_price_range` menuntut `valid_to > valid_from`. Menutup baris yang lahir hari ini
   * dengan `valid_to` hari ini melanggar itu — dan begitulah "→ semua kanal" dulu hanya mengisi
   * SEBAGIAN: setiap kanal yang harganya baru saja diketik justru yang terlewat. Persis kanal yang
   * paling mungkin baru diisi saat setup awal, dan penolakannya muncul sebagai "melanggar aturan
   * kolom" — kalimat yang tak bisa dihubungkan siapa pun dengan "harga ini baru saya isi tadi".
   *
   * ⚠️ Membuang baris hari ini AMAN: ia tak pernah melewati satu hari pun, jadi tak menyimpan
   * sejarah yang bisa ditanyakan. Penjualan yang terlanjur terjadi membawa harganya SENDIRI di
   * `order_line.unit_price_gross` — yang dibuang master-nya, bukan bukti transaksinya.
   *
   * ⛔ Baris dari hari SEBELUMNYA tetap DITUTUP, tak pernah dibuang. Di situlah jawaban atas
   * "berapa harganya bulan lalu?" tinggal, dan pertanyaan itu datang saat struk dibantah.
   */
  async function tulisHarga(productId: string, c: string, angka: string): Promise<void> {
    const hariIni = tanggalLokal();
    const grupId = target === null ? null : target.id;
    /*
     * ⚠️ Dibaca dari SERVER, bukan dari layar: kanal yang sedang disembunyikan belum tentu kosong,
     * dan menganggapnya kosong meninggalkan DUA harga berlaku bersamaan — yang dipakai kasir jadi
     * undian.
     */
    const lama = (await api.pricesByChannel(c)).find(
      (x) => x.product_id === productId && x.valid_to === null
        && x.outlet_id === null && x.price_group_id === grupId,
    );
    if (lama !== undefined) {
      if (lama.valid_from.slice(0, 10) === hariIni) await api.deletePrice(lama.id);
      else await api.closePrice(lama.id, hariIni);
    }
    await api.createPrice({
      productId,
      channel: c,
      price: `${angka}.0000`,
      validFrom: hariIni,
      /* ⛔ SATU sasaran per baris — `null` di sini berarti harga UMUM, bukan "lupa diisi". */
      ...(grupId === null ? {} : { priceGroupId: grupId }),
    });
  }

  async function simpan(b: Baris, c: string): Promise<void> {
    const k = kunci(b.product.id, c);
    const angka = (draft[k] ?? '').replace(/[^\d]/g, '');
    if (angka === '') return;
    setBusy(k);
    try {
      await tulisHarga(b.product.id, c, angka);
      await muat();
    } catch (e) {
      void message.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  /**
   * ⭐⭐ **Satu harga → SEMUA kanal** (Imron, 2026-08-04: *"tidak perlu satu-satu"*).
   *
   * ⛔⛔ Sesudahnya seluruh kanal DIPAKSA terlihat. Menulis ke kanal yang sedang disembunyikan
   * berarti mengubah angka yang tak bisa diperiksa penulisnya — dan yang tak bisa diperiksa akan
   * ditemukan pertama kali oleh tamu yang ditagih salah di GoFood.
   *
   * ⚠️ Kegagalan sebagian DISEBUT per kanal, bukan ditelan jadi satu "gagal". Enam kanal berarti
   * dua belas permintaan; kalau yang ketiga gagal, dua yang pertama SUDAH tertulis, dan layar yang
   * cuma bilang "gagal" membuat orang mengulang seluruhnya lalu menulis ganda.
   */
  async function replikasi(b: Baris): Promise<void> {
    const angka = (draft[`${b.product.id}|semua`] ?? '').replace(/[^\d]/g, '');
    if (angka === '') return;
    setBusy(`${b.product.id}|semua`);
    const gagal: string[] = [];
    try {
      for (const c of PRICE_CHANNELS) {
        try {
          await tulisHarga(b.product.id, c, angka);
        } catch {
          gagal.push(c);
        }
      }
      /* ⛔ Hasilnya WAJIB terlihat: seluruh kanal dinyalakan, lalu tabelnya dimuat ulang. */
      setKanal([...PRICE_CHANNELS]);
      if (gagal.length > 0) {
        void message.error(t('priceGroup.replicatePartial', { list: gagal.join(', ') }));
      } else {
        void message.success(t('priceGroup.replicateOk', { n: PRICE_CHANNELS.length }));
      }
    } finally {
      setBusy(null);
    }
  }

  const q = cari.trim().toLowerCase();
  const tampil = q === ''
    ? baris
    : baris.filter((b) => `${b.product.code} ${b.product.name}`.toLowerCase().includes(q));

  return (
    <Space orientation="vertical" size="small" style={{ width: '100%' }} data-testid="price-matrix">
      <Space wrap align="center">
        <Typography.Text strong>{t('priceGroup.target')}</Typography.Text>
        {/*
          ⭐⭐ "Harga umum" adalah SASARAN, bukan ketiadaan sasaran — dan ia yang pertama, karena
          itulah yang diisi lebih dulu saat menu baru dibangun. Grup hanya menimpanya.
        */}
        <Select<string>
          style={{ minWidth: 260 }}
          value={targetId}
          onChange={setTargetId}
          data-testid="harga-target"
          options={[
            { value: '', label: t('priceGroup.generalTarget') },
            ...groups.map((g) => ({ value: g.id, label: `${g.code} — ${g.name}` })),
          ]}
        />
        <Typography.Text strong>{t('priceGroup.channels')}</Typography.Text>
        <Select<string[]>
          mode="multiple"
          style={{ minWidth: 320 }}
          value={[...kanal]}
          onChange={(v) => { setKanal(v); }}
          data-testid="harga-kanal"
          /* ⚠️ Kode kanal ditampilkan MENTAH — portal-web memang begitu di seluruh layar master
             (jenis pajak, jenis produk). Menerjemahkannya di satu layar saja membuat orang mengira
             ia nilai yang berbeda dari yang dilihatnya di layar sebelah. */
          options={PRICE_CHANNELS.map((c) => ({ value: c, label: c }))}
        />
      </Space>

      <Alert
        type={target === null ? 'info' : 'warning'}
        showIcon
        data-testid="harga-catatan"
        title={target === null ? t('priceGroup.generalHint') : t('priceGroup.fillHint')}
      />

      <Input.Search
        allowClear
        placeholder={t('crud.search')}
        value={cari}
        onChange={(e) => { setCari(e.target.value); }}
        style={{ maxWidth: 320 }}
        data-testid="harga-cari"
      />

      {!memuat && baris.length === 0 ? (
        <Empty description={t('priceGroup.noProduct')} data-testid="harga-kosong" />
      ) : (
        <Table<Baris>
          size="small"
          loading={memuat}
          rowKey={(b) => b.product.id}
          dataSource={[...tampil]}
          pagination={{ pageSize: 25, showSizeChanger: false }}
          scroll={{ x: 'max-content' }}
          columns={[
            {
              title: t('master.code'),
              width: 140,
              fixed: 'left',
              render: (_: unknown, b: Baris) => (
                <b data-testid={`hm-${b.product.code}`}>{b.product.code}</b>
              ),
            },
            { title: t('master.name'), width: 200, render: (_: unknown, b: Baris) => b.product.name },
            {
              title: t('priceGroup.allChannels'),
              width: 240,
              render: (_: unknown, b: Baris) => {
                const k = `${b.product.id}|semua`;
                return (
                  <Space>
                    <Input
                      style={{ width: 120 }}
                      inputMode="numeric"
                      disabled={!boleh}
                      data-testid={`hm-semua-nilai-${b.product.code}`}
                      placeholder={t('priceGroup.allChannels')}
                      value={draft[k] ?? ''}
                      onChange={(e) => { setDraft((d) => ({ ...d, [k]: e.target.value })); }}
                    />
                    {/*
                      ⚠️ Konfirmasi, bukan langsung tulis — ia menyentuh ENAM kanal sekaligus, dan
                      teksnya menyebut risiko dagangnya: harga antar sekaligus disamakan dengan
                      dine-in berarti komisi penyelenggara (20–30%) dimakan dari margin sendiri.
                      Tombol yang mudah ditekan untuk hal yang mahal wajib menyebut mahalnya.
                    */}
                    <Popconfirm
                      title={t('priceGroup.replicateTitle')}
                      description={t('priceGroup.replicateWarning')}
                      okText={t('priceGroup.replicateOkBtn')}
                      cancelText={t('crud.cancel')}
                      onConfirm={() => void replikasi(b)}
                    >
                      <Button
                        size="small"
                        loading={busy === k}
                        disabled={!boleh || (draft[k] ?? '').trim() === ''}
                        data-testid={`hm-semua-simpan-${b.product.code}`}
                      >
                        {t('priceGroup.replicateBtn')}
                      </Button>
                    </Popconfirm>
                  </Space>
                );
              },
            },
            ...kanal.map((c) => ({
              title: c,
              width: 280,
              render: (_: unknown, b: Baris) => {
                const k = kunci(b.product.id, c);
                const kini = b.nilai[c];
                const pembanding = b.umum[c];
                return (
                  <Space orientation="vertical" size={2}>
                    <Space>
                      <Input
                        style={{ width: 120 }}
                        inputMode="numeric"
                        disabled={!boleh}
                        data-testid={`hm-nilai-${b.product.code}-${c}`}
                        placeholder={kini === undefined ? '—' : rp(kini.price)}
                        value={draft[k] ?? ''}
                        onChange={(e) => { setDraft((d) => ({ ...d, [k]: e.target.value })); }}
                      />
                      <Button
                        type="primary"
                        size="small"
                        loading={busy === k}
                        disabled={!boleh || (draft[k] ?? '').trim() === ''}
                        data-testid={`hm-simpan-${b.product.code}-${c}`}
                        onClick={() => void simpan(b, c)}
                      >
                        {t('crud.save')}
                      </Button>
                    </Space>
                    {/*
                      ⭐ Keadaan baris DINYATAKAN, tak pernah ditebak dari kotak kosong:
                      · sasaran grup & belum ditimpa → "ikut harga umum" (keadaan SAH)
                      · harga umum & belum ada       → "belum ada harga"  (yang TERLEWAT)
                      ⛔ Keduanya tak boleh terlihat sama — nol adalah harga yang sah, dan
                      menyamakan "belum diisi" dengan "Rp 0" menyembunyikan yang terlewat justru
                      di layar yang gunanya menemukannya.
                    */}
                    {kini !== undefined ? (
                      <Tag color="green" data-testid={`hm-punya-${b.product.code}-${c}`}>
                        {`Rp ${rp(kini.price)}`}
                      </Tag>
                    ) : target !== null ? (
                      <Tag data-testid={`hm-ikut-${b.product.code}-${c}`}>
                        {pembanding === undefined
                          ? t('priceGroup.noBasePrice')
                          : `${t('priceGroup.followsBase')} · Rp ${rp(pembanding.price)}`}
                      </Tag>
                    ) : (
                      <Tag color="orange" data-testid={`hm-tanpa-${b.product.code}-${c}`}>
                        {t('priceGroup.noBasePrice')}
                      </Tag>
                    )}
                  </Space>
                );
              },
            })),
          ]}
        />
      )}
    </Space>
  );
}
